import { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  CSVQuality,
  QualityAction,
  QualityResolution,
} from '@/types/source-types';
import { MetadataResolutionPayload } from '@/services/data-sources/submit-metadata-resolutions';

const ACTION_LABELS: Record<QualityAction, string> = {
  rename: 'Rename column',
  drop: 'Drop column',
  fill: 'Fill missing values',
};

interface CsvQualityResolverProps {
  quality: CSVQuality;
  headers?: string[];
  onSubmit: (payload: MetadataResolutionPayload[]) => Promise<void>;
  isSubmitting?: boolean;
}

interface IssueFormState {
  action?: QualityAction;
  value?: string;
}

type FormState = Record<number, IssueFormState>;
type ErrorState = Record<number, string>;

const deriveInitialValue = (resolution?: QualityResolution | null) => {
  if (!resolution) return undefined;
  return resolution.value ?? resolution.sanitized_value ?? '';
};

export const CsvQualityResolver = ({
  quality,
  headers,
  onSubmit,
  isSubmitting,
}: CsvQualityResolverProps) => {
  const issues = useMemo(() => quality?.issues ?? [], [quality?.issues]);
  const unresolvedIssues = useMemo(
    () => issues.filter((issue) => issue.status !== 'resolved'),
    [issues],
  );

  const initialFormState = useMemo<FormState>(() => {
    const state: FormState = {};
    issues.forEach((issue) => {
      const key = issue.column_index;
      const fromResolutions =
        quality?.resolutions?.[String(key)] ?? issue.resolution;
      if (fromResolutions) {
        state[key] = {
          action: fromResolutions.action as QualityAction,
          value: deriveInitialValue(fromResolutions),
        };
      }
    });
    return state;
  }, [issues, quality?.resolutions]);

  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [errors, setErrors] = useState<ErrorState>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const qualitySignature = useMemo(() => {
    const resolutionSignature = Object.entries(quality?.resolutions ?? {})
      .map(([key, resolution]) => {
        if (!resolution) return `${key}`;
        return `${key}:${resolution.action}:${resolution.value ?? ''}:${resolution.sanitized_value ?? ''}`;
      })
      .sort()
      .join('|');

    const issueSignature = issues
      .map((issue) => {
        const resolution = issue.resolution;
        return `${issue.column_index}:${issue.status}:${resolution?.action ?? ''}:${resolution?.value ?? ''}`;
      })
      .join('|');

    return `${quality?.applied_at ?? 'pending'}|${issueSignature}|${resolutionSignature}`;
  }, [issues, quality?.applied_at, quality?.resolutions]);

  const lastSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    if (lastSignatureRef.current === qualitySignature) {
      return;
    }
    lastSignatureRef.current = qualitySignature;
    setFormState(initialFormState);
    setErrors({});
    setSubmitError(null);
  }, [initialFormState, qualitySignature]);

  const availableActions = (issue: (typeof issues)[number]) => {
    const suggested = issue.suggested_actions?.length
      ? issue.suggested_actions
      : (['rename', 'drop', 'fill'] as QualityAction[]);
    return Array.from(new Set(suggested)) as QualityAction[];
  };

  const isFormComplete = unresolvedIssues.every((issue) => {
    const state = formState[issue.column_index];
    if (!state?.action) return false;
    if (state.action === 'rename' || state.action === 'fill') {
      return Boolean(state.value && state.value.trim());
    }
    return true;
  });

  const handleActionChange = (columnIndex: number, action: QualityAction) => {
    setFormState((prev) => ({
      ...prev,
      [columnIndex]: {
        action,
        value:
          action === 'rename' || action === 'fill'
            ? (prev[columnIndex]?.value ?? '')
            : undefined,
      },
    }));
    setErrors((prev) => {
      if (!prev[columnIndex]) return prev;
      const next = { ...prev };
      delete next[columnIndex];
      return next;
    });
  };

  const handleValueChange = (columnIndex: number, value: string) => {
    setFormState((prev) => ({
      ...prev,
      [columnIndex]: {
        ...prev[columnIndex],
        value,
      },
    }));
    setErrors((prev) => {
      if (!prev[columnIndex]) return prev;
      const next = { ...prev };
      delete next[columnIndex];
      return next;
    });
  };

  const handleSubmit = async () => {
    const nextErrors: ErrorState = {};
    const payload: MetadataResolutionPayload[] = [];

    unresolvedIssues.forEach((issue) => {
      const state = formState[issue.column_index];
      if (!state?.action) {
        nextErrors[issue.column_index] = 'Select an action to continue.';
        return;
      }

      if (state.action === 'rename') {
        if (!state.value || !state.value.trim()) {
          nextErrors[issue.column_index] = 'Enter a new column header.';
          return;
        }
        payload.push({
          column_index: issue.column_index,
          action: 'rename',
          new_header: state.value.trim(),
        });
        return;
      }

      if (state.action === 'fill') {
        if (state.value == null || !state.value.trim()) {
          nextErrors[issue.column_index] = 'Provide a default value to fill.';
          return;
        }
        payload.push({
          column_index: issue.column_index,
          action: 'fill',
          fill_value: state.value,
        });
        return;
      }

      payload.push({
        column_index: issue.column_index,
        action: 'drop',
      });
    });

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setErrors({});
    setSubmitError(null);

    try {
      await onSubmit(payload);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to save data quality fixes.';
      setSubmitError(message);
    }
  };

  if (!issues.length) {
    return null;
  }

  if (!quality.requires_resolution && unresolvedIssues.length === 0) {
    return (
      <div className='rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700 flex items-center gap-2'>
        <CheckCircle2 className='h-4 w-4 shrink-0' />
        All data quality issues for this file have been resolved.
      </div>
    );
  }

  return (
    <div className='space-y-3 rounded-md border border-amber-200 bg-amber-50 p-4'>
      <div className='flex items-start gap-2 text-sm text-amber-900'>
        <AlertTriangle className='h-4 w-4 mt-0.5 shrink-0' />
        <div>
          <p className='font-medium'>Fix data quality issues before syncing.</p>
          <p className='text-xs text-amber-900/80'>
            Schema and LLM sync are blocked until each issue below has a
            resolution.
          </p>
        </div>
      </div>

      <div className='space-y-3'>
        {issues.map((issue) => {
          const columnIndex = issue.column_index;
          const headerCandidates = headers || [];
          const headerLabel =
            headerCandidates[columnIndex] ??
            headerCandidates[columnIndex - 1] ??
            headerCandidates[columnIndex + 1] ??
            '';
          const action = formState[columnIndex]?.action;
          const value = formState[columnIndex]?.value || '';
          const actionOptions = availableActions(issue);
          const resolvedResolution =
            quality.resolutions?.[String(columnIndex)] ?? issue.resolution;
          const sanitizedValue = resolvedResolution?.sanitized_value;
          const showControls = issue.status !== 'resolved';

          return (
            <div
              key={`${issue.type}-${columnIndex}`}
              className='rounded-md border border-amber-200 bg-white p-3 shadow-sm space-y-2'
            >
              <div className='flex flex-wrap items-start justify-between gap-3 text-sm'>
                <div className='space-y-1 min-w-0'>
                  <p className='font-medium text-foreground'>
                    Column{' '}
                    {Number.isFinite(columnIndex) ? columnIndex + 1 : '—'}
                  </p>
                  <p className='text-xs text-muted-foreground truncate'>
                    Original header:{' '}
                    {headerLabel
                      ? headerLabel
                      : headerLabel === ''
                        ? '(empty)'
                        : '—'}
                  </p>
                </div>
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide',
                    issue.status === 'resolved'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-amber-100 text-amber-700',
                  )}
                >
                  {issue.status === 'resolved' ? 'Resolved' : 'Action needed'}
                </span>
              </div>
              {issue.message && (
                <p className='text-xs text-muted-foreground leading-snug'>
                  {issue.message}
                </p>
              )}
              {showControls ? (
                <div className='space-y-2'>
                  <div className='grid gap-2 md:grid-cols-[200px_1fr] md:items-start'>
                    <div className='space-y-1'>
                      <label className='text-xs font-medium text-muted-foreground'>
                        Suggested action
                      </label>
                      <Select
                        value={action}
                        onValueChange={(value) =>
                          handleActionChange(
                            columnIndex,
                            value as QualityAction,
                          )
                        }
                      >
                        <SelectTrigger className='h-8 text-sm'>
                          <SelectValue placeholder='Choose action' />
                        </SelectTrigger>
                        <SelectContent>
                          {actionOptions.map((option) => (
                            <SelectItem key={option} value={option}>
                              {ACTION_LABELS[option]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {(action === 'rename' || action === 'fill') && (
                      <div className='space-y-1'>
                        <label className='text-xs font-medium text-muted-foreground'>
                          {action === 'rename'
                            ? 'New column header'
                            : 'Default value to fill'}
                        </label>
                        {action === 'rename' ? (
                          <Input
                            value={value}
                            onChange={(event) =>
                              handleValueChange(columnIndex, event.target.value)
                            }
                            className='h-9 text-sm'
                          />
                        ) : (
                          <Textarea
                            value={value}
                            onChange={(event) =>
                              handleValueChange(columnIndex, event.target.value)
                            }
                            rows={2}
                            className='text-sm'
                          />
                        )}
                      </div>
                    )}
                  </div>
                  {errors[columnIndex] && (
                    <p className='text-xs text-destructive'>
                      {errors[columnIndex]}
                    </p>
                  )}
                </div>
              ) : (
                <div className='text-xs text-muted-foreground'>
                  <p>
                    Resolution applied:{' '}
                    {ACTION_LABELS[
                      (resolvedResolution?.action as QualityAction) ?? 'drop'
                    ] || resolvedResolution?.action}
                    {resolvedResolution?.value
                      ? ` — ${resolvedResolution.value}`
                      : ''}
                  </p>
                </div>
              )}
              {sanitizedValue && (
                <p className='text-[11px] text-muted-foreground'>
                  Saved header will be sanitized to:{' '}
                  <span className='font-medium'>{sanitizedValue}</span>
                </p>
              )}
            </div>
          );
        })}
      </div>

      {submitError && <p className='text-xs text-destructive'>{submitError}</p>}

      {unresolvedIssues.length > 0 && (
        <div className='flex items-center justify-end gap-2'>
          <Button
            type='button'
            onClick={handleSubmit}
            disabled={isSubmitting || !isFormComplete}
            className='h-8'
          >
            {isSubmitting ? 'Saving…' : 'Save fixes'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CsvQualityResolver;
